// File: src/wiring/entitlements/EntitlementResolver.ts
//
// EntitlementResolver (v1)
// ------------------------
// Canonical entitlement surface for wiring install decisions.
//
// Goals:
// - Single source of truth for tier comparisons
// - Default runtime resolver that works before auth/claims are wired
// - Easy to swap to Firebase custom claims / Stripe / enterprise SSO later
//

export type EntitlementTier = "public" | "creator" | "enterprise" | "regulated" | "unknown";

export type Entitlement = {
  tier: EntitlementTier;
  // room for growth:
  // orgId?: string;
  // claims?: Record<string, any>;
};

/**
 * Tier ordering for comparisons.
 * NOTE:
 * - "unknown" is intentionally last but treated as deny by allows()
 * - Ordering MUST remain stable if you rely on numeric comparisons downstream
 */
const ORDER: EntitlementTier[] = ["public", "creator", "enterprise", "regulated", "unknown"];

function normalizeTier(x: unknown): EntitlementTier {
  const s = String(x ?? "").trim().toLowerCase();
  return s === "public" || s === "creator" || s === "enterprise" || s === "regulated"
    ? (s as EntitlementTier)
    : "unknown";
}

/**
 * Compare whether entitlement tier satisfies required tier.
 * Rules:
 * - unknown never grants (safer default)
 * - required unknown is always deny (misconfigured manifest)
 */
export function allows(entitlement: Entitlement, required: EntitlementTier): boolean {
  const have = normalizeTier(entitlement?.tier);
  const need = normalizeTier(required);

  // If a manifest requires "unknown", treat as misconfigured and deny
  if (need === "unknown") return false;

  // Unknown never grants
  if (have === "unknown") return false;

  const hi = ORDER.indexOf(have);
  const ni = ORDER.indexOf(need);

  return hi >= 0 && ni >= 0 && hi >= ni;
}

/**
 * Default runtime resolver.
 * Current strategy:
 * - reads window.__synk_entitlement.tier (set by auth/payments layer)
 * - falls back to "public"
 *
 * Replace later with:
 * - Firebase custom claims (creator/enterprise)
 * - Tenant SSO claims
 * - Stripe entitlement cache
 */
export function resolveEntitlement(): Entitlement {
  // SSR/test safe guard
  if (typeof window === "undefined") return { tier: "public" };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w: any = window as any;

  const tier = normalizeTier(w?.__synk_entitlement?.tier);

  // Safe default: public
  return { tier: tier === "unknown" ? "public" : tier };
}
